import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { headerSaga } from 'app/pages/Header/saga';
import { reducer, sliceKey } from 'app/pages/Header/slice';
import ValueContentService, {
  ValueContent,
} from 'app/apiService/valueContent/valueContentApiService';

// API Response types
interface ApiMeta {
  totalDocuments: number;
  totalPages: number;
  currentPage: number;
}

interface ApiResponse {
  data: ValueContent[];
  meta: ApiMeta;
}

// ----------- Type Definitions -----------
interface ValueContentContextType {
  loading: boolean;
  error: string;
  valueContent: ValueContent[];
  selectedContent: ValueContent | null;

  // Dealer selection
  selectedDealerId: string;
  selectedDealerName: string;

  // Infinite scroll properties
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  totalContent: number;
  currentPage: number;

  addValueContent: (content: any) => Promise<void>;
  deleteValueContent: (contentId: string) => Promise<void>;
  editValueContent: (contentId: string, data: any) => Promise<void>;
  getValueContent: (contentId: string) => Promise<void>;
  refetchValueContent: () => void;
  resetAndRefetchValueContent: () => void;
  enableQuery: () => void;

  // Filters for infinite query
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  sortBy: string;
  setSortBy: (sort: string) => void;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  typeFilter: string;
  setTypeFilter: (type: string) => void;
  isActiveFilter: boolean | null;
  setIsActiveFilter: (isActive: boolean | null) => void;
}

export const ValueContentContext = createContext<
  ValueContentContextType | undefined
>(undefined);

// Custom hook for debouncing
const useDebounce = (value: any, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const ValueContentProvider = ({ children }: { children: ReactNode }) => {
  const [error, setError] = useState<string>('');
  const [selectedContent, setSelectedContent] = useState<ValueContent | null>(
    null,
  );
  const [isQueryEnabled, setIsQueryEnabled] = useState<boolean>(false);
  const queryClient = useQueryClient();

  // Inject Header reducer and saga for dealer functionality
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });

  // Get selected dealer from header state
  const selectedDealer = useSelector(selectDealerSelected);
  const selectedDealerId = selectedDealer?.dealer || '';

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | null>(true);

  // Debounced values for API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const debouncedTypeFilter = useDebounce(typeFilter, 300);
  const debouncedIsActiveFilter = useDebounce(isActiveFilter, 300);
  const debouncedStatusFilter = useDebounce(statusFilter, 300);
  const debouncedSortBy = useDebounce(sortBy, 300);

  const ITEMS_PER_PAGE = 9;

  // Mock data for fallback
  const mockValueContent: ValueContent[] = [
    {
      _id: '1',
      headline: 'The Toyota Gold Certified Difference',
      content:
        'Only the best vehicles qualify as Toyota Certified Used, undergoing a rigorous 160-point inspection for complete peace of mind.\n\nEach certified vehicle includes a 12-month/12,000-mile Limited Comprehensive Warranty, a 7-year/100,000-mile Limited Powertrain Warranty with a $0 deductible, plus 7-year/100,000-mile Roadside Assistance and more*.',
      value_type: 'CPO',
      applicable_for: ['CPO'],
      end_date: '',
      is_active: true,
      dealership_id: 'dealer_1',
      label: 'Certified Pre-Owned',
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
    },
    {
      _id: '2',
      headline: 'TOYOTACARE* PEACE OF MIND COMES STANDARD',
      content:
        'As part of the Toyota family, your ownership experience begins with ToyotaCare*—a no-cost maintenance plan and 24-hour roadside assistance for 2 years or 25,000 miles, whichever comes first.\n\nToyotaCare includes engine oil and filter changes, tire rotations, multi-point inspections, and fluid checks, all performed by factory-trained technicians using Toyota Genuine Parts.\n\nServices are available at no cost when completed at Wilder Toyota.\n\nStart your car-buying journey by browsing our inventory, valuing your trade, or applying for financing below.',
      value_type: 'OEM Value',
      applicable_for: ['New'],
      end_date: '',
      is_active: true,
      dealership_id: 'dealer_1',
      label: 'ToyotaCare Service',
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
    },
    {
      _id: '3',
      headline: 'Get the Best Value for your Current Vehicle',
      content:
        "Let us help you turn your current vehicle into real value—quickly and confidently. We understand that every trade-in situation is unique, and we'll take the time to provide a fair, no-obligation evaluation.\n\nWhy trade with Wilder Toyota?\n• Fast and accurate offers through KBB Instant Cash Offer\n• No obligation—redeem for cash or apply toward a new purchase\n• Trusted appraisals from our experienced team\n• Streamlined trade-in process with minimal paperwork\n• Competitive pricing on a wide selection of new and pre-owned vehicles\n\nMany of our customers are surprised at just how much their vehicle is worth in today's market. Plus, by using KBB's trusted system, you'll enjoy a streamlined experience that eliminates the guesswork. Start today online or simply stop by our dealership for a free evaluation.",
      value_type: 'TradeIn',
      applicable_for: ['All'],
      end_date: '',
      is_active: true,
      dealership_id: 'dealer_1',
      label: 'Trade-In Value',
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
    },
  ];

  // Convert filters to DataGrid format
  const convertFiltersToDataGridFormat = (
    searchTerm: string,
    statusFilter: string,
    type: string,
    isActive: boolean | null,
  ) => {
    const filters: any = {};

    if (searchTerm) {
      filters.$or = [
        { headline: { $regex: searchTerm, $options: 'i' } },
        { content: { $regex: searchTerm, $options: 'i' } },
        { value_type: { $regex: searchTerm, $options: 'i' } },
      ];
    }

    if (statusFilter) {
      filters.status = statusFilter;
    }

    if (type) {
      filters.value_type = type;
    }

    if (isActive !== null) {
      filters.is_active = isActive;
    }

    return Object.keys(filters).length > 0 ? JSON.stringify(filters) : '';
  };

  // Convert sort to DataGrid format
  const convertSortToDataGridFormat = (sortBy: string) => {
    if (!sortBy) return '';

    const sortMap: { [key: string]: string } = {
      headline: 'headline',
      value_type: 'value_type',
      is_active: 'is_active',
      createdAt: 'createdAt',
      updatedAt: 'updatedAt',
    };

    const [field, direction] = sortBy.split(' ');
    const mappedField = sortMap[field] || field;

    return JSON.stringify([{ field: mappedField, sort: direction || 'asc' }]);
  };

  // Instead, just provide empty valueContent (or mockValueContent if you want UI to show something)
  const valueContent: ValueContent[] = [];

  // CRUD operations
  const addValueContent = async (content: any) => {
    try {
      const payload = {
        headline: content.headline,
        content: content.content,
        value_type: content.value_type,
        is_active: content.is_active,
        end_date: content.end_date || '',
        dealership_id: selectedDealerId,
        applicable_for: content.applicable_for || [],
        label: content.label || undefined,
      };
      await ValueContentService.addValueContent(payload);
      //   await refetch();
    } catch (error: any) {
      setError(error.message || 'Failed to add value content');
      throw error;
    }
  };

  const deleteValueContent = async (contentId: string) => {
    try {
      await ValueContentService.deleteValueContent(contentId);
      //   await refetch();
    } catch (error: any) {
      setError(error.message || 'Failed to delete value content');
      throw error;
    }
  };

  const editValueContent = async (contentId: string, data: any) => {
    try {
      await ValueContentService.editValueContent(contentId, data);
      //   await refetch();
    } catch (error: any) {
      setError(error.message || 'Failed to edit value content');
      throw error;
    }
  };

  const getValueContent = async (contentId: string) => {
    try {
      const response = await ValueContentService.getValueContent(contentId);
      setSelectedContent(response.body as ValueContent);
    } catch (error: any) {
      setError(error.message || 'Failed to get value content details');
      throw error;
    }
  };

  const refetchValueContent = useCallback(() => {
    // refetch(); // This line is removed as per the edit hint
  }, []);

  const resetAndRefetchValueContent = useCallback(() => {
    setSearchTerm('');
    setSortBy('');
    setStatusFilter('');
    setTypeFilter('');
    setIsActiveFilter(true);
    // refetch(); // This line is removed as per the edit hint
  }, []);

  const enableQuery = useCallback(() => {
    console.log('enableQuery called, setting isQueryEnabled to true');
    setIsQueryEnabled(true);
  }, []);

  const contextValue: ValueContentContextType = {
    loading: false,
    error: '',
    valueContent,
    selectedContent,
    selectedDealerId,
    selectedDealerName: selectedDealer?.site_title || '',
    hasNextPage: false,
    isFetchingNextPage: false,
    fetchNextPage: () => {},
    totalContent: 0,
    currentPage: 1,
    addValueContent,
    deleteValueContent,
    editValueContent,
    getValueContent,
    refetchValueContent,
    resetAndRefetchValueContent,
    enableQuery,
    searchTerm,
    setSearchTerm,
    sortBy,
    setSortBy,
    statusFilter,
    setStatusFilter,
    typeFilter,
    setTypeFilter,
    isActiveFilter,
    setIsActiveFilter,
  };

  return (
    <ValueContentContext.Provider value={contextValue}>
      {children}
    </ValueContentContext.Provider>
  );
};

export const useValueContent = (): ValueContentContextType => {
  const context = useContext(ValueContentContext);
  if (context === undefined) {
    throw new Error(
      'useValueContent must be used within a ValueContentProvider',
    );
  }
  return context;
};
